<template>
	<div class="relative flex-shrink-0 w-[208px] transition-all duration-100 bg-transparent h-[277px] rounded-xl snap-center snap-always">
		<div
			v-track-event:custom.click="customTracker"
			:class="[
				'absolute top-0 left-0 w-full h-full transition-all duration-200 rounded-xl shadow-xl',
				isEnabled ? 'cursor-pointer' : 'cursor-not-allowed',
				isActiveCard ? 'z-10' : '',
				shadow
			]"
			:style="{ marginLeft: setMarginLeft(), zIndex: setZIndex(), scale: setScale(), background: setBackground() }"
			@click="handleCardClick"
		>
			<div class="relative w-full h-full">
				<LeafIcon
					v-if="!isOpened"
					:fill-opacity="isEnabled ? '0.6' : '0.2'"
					:class="[
						'absolute w-full transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2 z-2',
						isEnabled ? 'animate-pulse' : 'text-gray-500'
					]"
				/>
				<LeafIcon
					v-if="!isOpened && isEnabled"
					class="absolute w-full transform scale-75 -translate-x-1/2 -translate-y-1/2 bg-yellow-500 rounded-full top-1/2 left-1/2 z-1 blur-2xl animate-pulse opacity-70"
				/>
				<div class="absolute inset-x-0 top-0 grid grid-cols-1 text-center z-3">
					<div
						:class="[
							isEnabled ? 'text-white' : 'text-gray-500',
							'pt-[80px] font-bold uppercase text-[22px]'
						]"
					>
						{{ dabDay.month }} {{ isOpened ? dabDay.date : '' }}
					</div>
					<div
						v-if="!isOpened"
						:class="[
							isActiveCard ? 'text-[134px]' : 'text-[115px]',
							isEnabled ? 'text-white' : 'text-gray-500',
							'pt-[54px] font-bold transition-all duration-100 ease-in-out',
						]"
					>
						{{ dabDay.date }}
					</div>
					<div
						v-else
						class="px-3 pt-4"
					>
						Thanks for visiting, Share with your friends!
					</div>
				</div>
				<img
					v-if="isEnabled"
					:src="bowImage"
					:height="bowImageHeight"
					:width="bowImageWidth"
					class="absolute top-0 left-0 transform rounded-tl-xl z-5"
				>
			</div>
		</div>
	</div>
</template>

<script>
import {
	mapMutations
} from 'vuex'

import LeafIcon from '@/components/icons/LeafIcon.vue'
import { useDabvent } from '@/composables/dabvent.js'
import CAROUSEL_ITEM from '@/constants/carousel/carousel-item.js'
import { DAB_DAY_CUSTOM_MAP } from '@/constants/logging/customEvents.js'
import { DABVENT_MODAL } from '@/constants/modal/names.js'

const { setViewed, isDayViewable } = useDabvent()

export default {
	components: {
		LeafIcon
	},
	props: {
		index: {
			type: Number,
			default: 1
		},
		activeIndex: {
			type: Number,
			default: 1
		},
		totalCount: {
			type: Number,
			default: 1
		},
		isDesktop: {
			type: Boolean,
			default: false
		},
		dabDay: {
			type: Object,
			required: true
		}
	},
	emits: [
		'set-active-slide'
	],
	data() {
		return {
			CAROUSEL_ITEM,
			leafBackgroundImg: require('@/assets/backgrounds/white-leaf.svg'),
			unOpenedGradient: 'linear-gradient(180deg, #A03333 0%, #FC3333 100.08%)',
			openedGradient: '#992020',
			disabledBgColor: '#F1F5F8'
		}
	},
	computed: {
		shadow() {
			if (!this.isEnabled) return ''
			return !this.isOpened && this.isActiveCard ? 'shadow-yellow-500' : 'shadow-red-500'
		},
		isActiveCard() {
			return this.index === this.activeIndex
		},
		afterActiveSlide() {
			return this.index > this.activeIndex
		},
		absolutePositionRelatedToActiveSlide() {
			return Math.abs(this.index - this.activeIndex)
		},
		isEnabled() {
			return isDayViewable(this.dabDay?.day)
		},
		bowImage() {
			if (this.isOpened) return require('@/assets/illustrations/gift-bow-open.svg')
			return require('@/assets/illustrations/gift-bow.svg')
		},
		bowImageWidth() {
			if (this.isOpened) return 208
			return 117
		},
		bowImageHeight() {
			if (this.isOpened) return 60
			return 103
		},
		isOpened() {
			return this.dabDay?.viewed
		},
		customTracker() {
			if (this.isEnabled && this.isActiveCard) { // track click only if the card is enabled and modal is opened
				return {
					customMap: DAB_DAY_CUSTOM_MAP,
					event: {
						name: 'Dabvent',
						params: {
							paramName: 'value',
							paramValue: this.dabDay
						}
					}
				}
			}
			return null // pass null to disable tracking
		}
	},
	methods: {
		...mapMutations('modal', [ 'showModal' ]),
		setBackground() {
			if (!this.isEnabled) return this.disabledBgColor
			if (this.isOpened) return this.openedGradient
			return this.unOpenedGradient
		},
		handleCardClick() {
			if (!this.isEnabled) return
			if (this.isActiveCard) setViewed(this.dabDay?.day)
			if (this.isOpened && this.isActiveCard) {
				this.showModal(DABVENT_MODAL)
				return
			}
			if (!this.isDesktop) this.setActiveIndex()
		},
		setActiveIndex() {
			if (this.isActiveCard) this.$emit('set-active-slide', null)
			else this.$emit('set-active-slide', this.index + 1)
		},
		setMarginLeft() { // sets the amount of overlap between cards
			const BASE_OFFSET = 80
			const isFirstCardBeforeOrAfterActiveSlide = this.absolutePositionRelatedToActiveSlide === 1
			const CALCULATED_OFFSET = isFirstCardBeforeOrAfterActiveSlide ? BASE_OFFSET : BASE_OFFSET + (this.totalCount / 2) * this.absolutePositionRelatedToActiveSlide
			if (this.afterActiveSlide) return `-${this.absolutePositionRelatedToActiveSlide * CALCULATED_OFFSET}px` // negative margin if positioned to the right of current card
			return `${this.absolutePositionRelatedToActiveSlide * CALCULATED_OFFSET}px` // positive margin if positioned to the left of current card
		},
		setZIndex() {
			return `${this.totalCount - this.absolutePositionRelatedToActiveSlide}`
		},
		setScale() { // sets the scale of the card in relation to the active card by 0.1 increments
			if (this.isActiveCard) return 1.2 // increase the scale of active card for visual separation
			if (this.absolutePositionRelatedToActiveSlide > 9) return 0
			return 1 - this.absolutePositionRelatedToActiveSlide * 0.1
		}
	}
}
</script>
